import { motion, useScroll, useMotionValueEvent, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import "./Header.css";
import TextType from '../textType/TextType';
import { useScrollContainer } from "../customScrollbar/ScrollContext";

export default function Header() {
  const { scrollY } = useScroll();
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("top");
  const [menuOpen, setMenuOpen] = useState(false);

  useMotionValueEvent(scrollY, "change", (latest) => {
    const previous = scrollY.getPrevious();
    setScrolled(latest > 10);
    if (latest > previous && latest > 100) {
      setHidden(true);
    } else {
      setHidden(false);
    }
  });

  // Bloqueia o scroll do body quando o menu está aberto
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  useEffect(() => {
    const sections = ["top", "about", "skills", "projects", "contact"];

    const observers = sections.map((id) => {
      const el = document.getElementById(id);
      if (!el) return null;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setActiveSection(id);
          }
        },
        {
          rootMargin: "-40% 0px -55% 0px",
          threshold: 0,
        }
      );

      observer.observe(el);
      return observer;
    });

    return () => {
      observers.forEach((obs, i) => {
        const el = document.getElementById(sections[i]);
        if (obs && el) obs.unobserve(el);
      });
    };
  }, []);

  const scrollContainer = useScrollContainer();

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setMenuOpen(false);
    const target = document.querySelector(href);
    const el = scrollContainer?.current;
    if (target && el) {
      const navHeight = 70;
      const top = target.offsetTop - navHeight;
      el.scrollTo({ top, behavior: "smooth" });
    }
  };

  const handleClick = () => {
    window.open("/cv.pdf", "_blank");
    const link = document.createElement("a");
    link.href = "/cv.pdf";
    link.download = "CV_EmanuelSoares.pdf";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const navLinks = [
    { href: "#top", label: "Home", section: "top" },
    { href: "#about", label: "About", section: "about" },
    { href: "#projects", label: "Projects", section: "projects" },
    { href: "#contact", label: "Contact", section: "contact" },
  ];

  const menuVariants = {
    closed: {
      clipPath: "circle(0% at calc(100% - 40px) 40px)",
      opacity: 0,
      transition: {
        duration: 0.5,
        ease: [0.4, 0, 0.2, 1],
        opacity: { delay: 0.3, duration: 0.2 },
      },
    },
    open: {
      clipPath: "circle(150% at calc(100% - 40px) 40px)",
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: [0.4, 0, 0.2, 1],
        opacity: { duration: 0.1 },
      },
    },
  };

  const listVariants = {
    closed: {},
    open: {
      transition: { staggerChildren: 0.08, delayChildren: 0.25 },
    },
  };

  const itemVariants = {
    closed: { opacity: 0, x: 40 },
    open: { opacity: 1, x: 0, transition: { duration: 0.4, ease: "easeOut" } },
  };

  return (
    <>
      <motion.header
        className={`header ${scrolled ? "header--scrolled" : ""}`}
        initial={{ opacity: 0, y: -40 }}
        variants={{
          visible: { y: 0, opacity: 1 },
          hidden: { y: -100, opacity: 0 },
        }}
        animate={hidden && !menuOpen ? "hidden" : "visible"}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <div className="header-left">
          <TextType
            text={["<Emanuel Soares />"]}
            className="logo"
            typingSpeed={75}
            pauseDuration={5000}
            showCursor
            cursorCharacter="_"
            deletingSpeed={50}
            variableSpeedEnabled={false}
            variableSpeedMin={60}
            variableSpeedMax={120}
            cursorBlinkDuration={0.5}
          />
        </div>

        {/* Desktop nav */}
        <nav className="header-right">
          {navLinks.map(({ href, label, section }) => (
            <a
              key={href}
              href={href}
              onClick={(e) => handleNavClick(e, href)}
              className={
                activeSection === section ||
                (section === "about" && activeSection === "skills")
                  ? "active"
                  : ""
              }
            >
              {label}
            </a>
          ))}
          <div className="headerDivider"></div>
          <button onClick={handleClick} className="download-btn">
            Download CV
          </button>
        </nav>

        {/* Hamburger button (mobile only) */}
        <button
          className={`hamburger ${menuOpen ? "hamburger--open" : ""}`}
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-label="Toggle menu"
        >
          <span className="hamburger__bar" />
          <span className="hamburger__bar" />
          <span className="hamburger__bar" />
        </button>
      </motion.header>

      {/* Mobile fullscreen overlay */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="mobile-menu"
            initial="closed"
            animate="open"
            exit="closed"
            variants={menuVariants}
          >
            <motion.nav
              className="mobile-menu__nav"
              variants={listVariants}
              initial="closed"
              animate="open"
              exit="closed"
            >
              {navLinks.map(({ href, label, section }, i) => (
                <motion.a
                  key={href}
                  href={href}
                  variants={itemVariants}
                  className={`mobile-menu__link ${
                    activeSection === section ||
                    (section === "about" && activeSection === "skills")
                      ? "mobile-menu__link--active"
                      : ""
                  }`}
                  onClick={(e) => handleNavClick(e, href)}
                >
                  <span className="mobile-menu__index">0{i + 1}</span>
                  {label}
                </motion.a>
              ))}

              <motion.div variants={itemVariants} className="mobile-menu__cta">
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    handleClick();
                  }}
                  className="mobile-menu__download"
                >
                  Download CV
                </button>
              </motion.div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}